/**
 * The device card's behaviour (settings part 2a, 2026-10-11): "notifications on this device".
 *
 * Moved verbatim out of src/components/common/NotificationPermissionCard.tsx, whose default export (the live container)
 * now only draws this hook's answer: who Firebase says is signed in; this browser's notification state and platform,
 * read again on every return to the tab (permission can change in the OS or the browser's site settings); the request,
 * which runs inside the click (the only moment the browser accepts the prompt), never on mount; the re-check; the
 * account's registered devices, watched only while notifications are allowed; and removing one. Today's card and the
 * new design's settings page draw from the same hook, so the two can never ask, check or remove differently.
 *
 * Never a disabled control is the card's rule, and it holds here: `busy` says a request or re-check is in flight, and
 * the press stays live.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { getAuth, onAuthStateChanged } from 'firebase/auth';
import { onSnapshot } from 'firebase/firestore';
import { Paths } from '../repositories/firestoreUtils';
import {
  detectNotificationPlatform,
  getNotificationState,
  readNotificationEnvironment,
  requestPushPermission,
  type NotificationPlatform,
  type NotificationState,
} from '../services/notificationCapability';
import {
  hasPushTokenForUid,
  readCachedPushRegistration,
  refreshPushRegistrationIfGranted,
  removeDeviceToken,
} from '../services/PushRegistration';
import type { NotificationDeviceRow } from './notificationPermissionModel';

export interface NotificationPermission {
  state: NotificationState;
  platform: NotificationPlatform;
  /** A request or re-check is in flight; the control stays live and says so. */
  busy: boolean;
  devices: NotificationDeviceRow[];
  onRequest: () => void;
  onRecheck: () => void;
  /** Removing a device needs a signed-in person; undefined while there is none. */
  onRemoveDevice?: (token: string) => void;
}

function currentState(uid: string | null): { state: NotificationState; platform: NotificationPlatform } {
  const env = readNotificationEnvironment();
  return {
    state: getNotificationState(env, { hasToken: hasPushTokenForUid(uid) }),
    platform: detectNotificationPlatform(env),
  };
}

export function useNotificationPermission(): NotificationPermission {
  const [uid, setUid] = useState<string | null>(() => {
    try { return getAuth().currentUser?.uid || null; } catch { return null; }
  });
  useEffect(() => {
    try {
      return onAuthStateChanged(getAuth(), (u) => setUid(u?.uid || null));
    } catch {
      return undefined;
    }
  }, []);

  const [snapshot, setSnapshot] = useState(() => currentState(uid));
  const [busy, setBusy] = useState(false);
  const recheck = useCallback(() => {
    setSnapshot(currentState(uid));
  }, [uid]);

  // Permission can change outside the page (OS settings, browser site
  // settings): re-read on every return to the tab.
  useEffect(() => {
    recheck();
    const onVisible = () => { if (document.visibilityState === 'visible') recheck(); };
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', onVisible);
    return () => {
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', onVisible);
    };
  }, [recheck]);

  const handleRequest = useCallback(() => {
    // Inside the click — the only moment the browser accepts the prompt.
    setBusy(true);
    void requestPushPermission()
      .then((state) => setSnapshot((prev) => ({ ...prev, state })))
      .catch(() => recheck())
      .finally(() => setBusy(false));
  }, [recheck]);

  const handleRecheck = useCallback(() => {
    setBusy(true);
    void refreshPushRegistrationIfGranted({ force: true })
      .catch(() => null)
      .finally(() => {
        recheck();
        setBusy(false);
      });
  }, [recheck]);

  // The account's registered devices — only while it matters.
  const [devices, setDevices] = useState<NotificationDeviceRow[]>([]);
  const showDevices = snapshot.state === 'granted' || snapshot.state === 'granted-no-token';
  useEffect(() => {
    if (!uid || !showDevices) {
      setDevices([]);
      return undefined;
    }
    let unsubscribe: (() => void) | null = null;
    try {
      unsubscribe = onSnapshot(
        Paths.userDeviceTokensCol(uid),
        (snap) => {
          const mine = readCachedPushRegistration();
          setDevices(snap.docs.map((d) => {
            const data = d.data() as Record<string, unknown>;
            const seen = data.last_seen as { toDate?: () => Date } | undefined;
            return {
              token: d.id,
              platform: typeof data.platform === 'string' ? data.platform : undefined,
              os: typeof data.os === 'string' ? data.os : undefined,
              browser: typeof data.browser === 'string' ? data.browser : undefined,
              standalone: data.standalone === true,
              lastSeen: seen && typeof seen.toDate === 'function' ? seen.toDate() : null,
              isThisDevice: Boolean(mine && mine.uid === uid && mine.token === d.id),
            };
          }));
        },
        () => setDevices([]),
      );
    } catch {
      setDevices([]);
    }
    return () => { if (unsubscribe) unsubscribe(); };
  }, [uid, showDevices]);

  const handleRemoveDevice = useCallback((token: string) => {
    if (!uid) return;
    void removeDeviceToken(uid, token).catch(() => undefined).finally(recheck);
  }, [uid, recheck]);

  const viewProps = useMemo(() => ({
    state: snapshot.state,
    platform: snapshot.platform,
  }), [snapshot.state, snapshot.platform]);

  return {
    ...viewProps,
    busy,
    devices,
    onRequest: handleRequest,
    onRecheck: handleRecheck,
    onRemoveDevice: uid ? handleRemoveDevice : undefined,
  };
}
